/**
 * Whether the player asks to run (ADR-116 decisions 8 and 11, P2.20-T6), as pure rules: `runIntent` joins the Run
 * setting with the Shift key and the toggle's switch, and `toggleAfter` is the switch's reducer over the key events
 * the input router shows. The page only asks: the map server alone says whether a step is a run step.
 */

/**
 * The Run setting's two choices: running while Shift is down, or a switch Shift flips.
 */
export type RunMode = "hold" | "toggle";

/**
 * The mode a page has before a player chooses (ADR-116 decision 11).
 */
export const DEFAULT_RUN_MODE: RunMode = "hold";

/**
 * Whether the player asks to run now.
 *
 * @param mode The Run setting.
 * @param shiftDown Whether Shift is held, as the game's own keyboard reads it.
 * @param toggled Whether the toggle's switch is on.
 * @returns True when the next input with a direction should carry the sprint bit.
 * @example
 * runIntent("hold", true, false);
 * // true
 */
export function runIntent(mode: RunMode, shiftDown: boolean, toggled: boolean): boolean {
  return mode === "toggle" ? toggled : shiftDown;
}

/**
 * The toggle's switch: whether running is on, and whether a Shift pressed in the game is still down with no other
 * key pressed since, so that its release flips the switch.
 */
export interface ToggleState {
  readonly on: boolean;
  readonly armed: boolean;
}

/**
 * The switch as a page starts with it: off.
 */
export const TOGGLE_OFF: ToggleState = Object.freeze({ on: false, armed: false });

const ON: ToggleState = Object.freeze({ on: true, armed: false });
const OFF_ARMED: ToggleState = Object.freeze({ on: false, armed: true });
const ON_ARMED: ToggleState = Object.freeze({ on: true, armed: true });

/**
 * What the switch hears. `down` and `up` are a key pressed and released: whether it is Shift, and whether the game
 * owned the keyboard then (no text field focused and no modal holding it); a press also says whether it is a held
 * key's auto-repeat. `lost` is the keyboard or the window leaving the game. `off` is the page turning running off:
 * the character is spent or dead, or the world was loaded again.
 */
export type ToggleEvent = { readonly kind: "down"; readonly shift: boolean; readonly owned: boolean; readonly repeat: boolean } | { readonly kind: "up"; readonly shift: boolean; readonly owned: boolean } | { readonly kind: "lost" } | { readonly kind: "press" } | { readonly kind: "off" };

/**
 * One of the four states, so the reducer makes nothing.
 *
 * @param on Whether running is on.
 * @param armed Whether a release of Shift would flip it.
 * @returns The state.
 */
function stateOf(on: boolean, armed: boolean): ToggleState {
  if (on) {
    return armed ? ON_ARMED : ON;
  }
  return armed ? OFF_ARMED : TOGGLE_OFF;
}

/**
 * The switch after an event (ADR-116 decision 11): a press of Shift in the game arms it, its release there flips it,
 * and any other key pressed between disarms it, so Shift held for Shift+I, Shift+E, Shift+Tab or a capital never
 * flips it; a pointer press between disarms it the same way, so Shift held through a click (a split in the bag, ADR-091's
 * note for P2.8-T14 of the author's controls) never flips it. A Shift pressed or released while something else has the keyboard, and a loss of the keyboard between,
 * flip nothing and leave running as it was. `off` turns it off and disarms it. An auto-repeat changes nothing. An
 * event that changes nothing answers the state it was given.
 *
 * @param state The switch before.
 * @param event What happened.
 * @returns The switch after.
 * @example
 * toggleAfter(toggleAfter(TOGGLE_OFF, { kind: "down", shift: true, owned: true, repeat: false }), { kind: "up", shift: true, owned: true }).on;
 * // true
 */
export function toggleAfter(state: ToggleState, event: ToggleEvent): ToggleState {
  switch (event.kind) {
    case "down":
      if (event.repeat) {
        return state;
      }
      return stateOf(state.on, event.shift && event.owned);
    case "up":
      if (!event.shift) {
        return state;
      }
      return stateOf(state.armed && event.owned ? !state.on : state.on, false);
    case "lost":
    case "press":
      return stateOf(state.on, false);
    case "off":
      return TOGGLE_OFF;
  }
}
